import React, { FC, useRef } from "react";
import type { HomeHeroProps } from "@/types";
import { ContentManager } from "../content-manager";
import { Link } from "../link";
import SplitTextAnimation from "../split-text-animation";
import { useInView } from "@/hooks/reveal-in-view";
import { useResponsiveVideoSource } from "../../hooks";

export const HomeHero: FC<HomeHeroProps> = ({
  title,
  description,
  getInTouch,
  discover,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px 25% 0px" });
  const videoRef = useRef<HTMLVideoElement>(null);
  useResponsiveVideoSource(videoRef, {
    desktopSrc: "/video/home-hero-desktop.mp4",
    mobileSrc: "/video/home-hero-mobile.mp4",
  });

  return (
    <section className="homeHero">
      <div className="wrapper">
        <div className="homeHero--inner">
          <div ref={ref} className="homeHero--content">
            <SplitTextAnimation
              htmlElement={
                <h1
                  style={{
                    opacity: isInView ? 1 : 0,
                  }}
                >
                  {title}
                </h1>
              }
              isInView={isInView}
              className="homeHero--title"
            />
            <ContentManager
              content={description}
              className="homeHero--description"
            />
            <div className="homeHero--actions">
              <Link
                href={getInTouch.link?.uri as string}
                theme="white"
                fill="full"
                title={getInTouch.text}
              >
                <span>{getInTouch.text}</span>
              </Link>
              <Link
                href={discover.link?.uri as string}
                theme="white"
                fill="outline"
                title={discover.text}
              >
                <span>{discover.text}</span>
              </Link>
            </div>
          </div>
          <div className="homeHero--visual">
            {/* A single <video> with media-scoped sources: browsers download
                only the matching file. Two CSS-toggled <video autoPlay>
                elements made every visitor download both (~5.8 MB). */}
            <video
              ref={videoRef}
              muted
              autoPlay
              loop
              playsInline
              className="homeHero--video"
            >
              <source
                src="/video/home-hero-desktop.mp4"
                type="video/mp4"
                media="(min-width: 1024px)"
              />
              <source src="/video/home-hero-mobile.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </div>
    </section>
  );
};
